import React, { useState, useEffect } from 'react';
import {
  Trophy,
  Search,
  Plus,
  X,
  Edit2,
  Trash2,
  Award
} from 'lucide-react';
import api from '../../utils/api';
import './AchievementsPage.css';

const AchievementsPage = () => {
  // State for stats
  const [stats, setStats] = useState({
    totalAchievements: 0,
    byCategory: {}
  });

  // State for achievements
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    searchStudent: '',
    category: '',
    level: ''
  });

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    studentId: '',
    title: '',
    category: '',
    level: '',
    position: '',
    date: '',
    description: ''
  });

  // Constants
  const CATEGORIES = ['academic', 'sports', 'cultural', 'science', 'other'];
  const LEVELS = ['school', 'district', 'state', 'national'];
  const CATEGORY_COLORS = {
    academic: '#3b82f6',
    sports: '#10b981',
    cultural: '#b8860b',
    science: '#f472b6',
    other: '#6b7280'
  };

  const [students, setStudents] = useState([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/achievements/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch achievements
  const fetchAchievements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.searchStudent) params.append('student', filters.searchStudent);
      if (filters.category) params.append('category', filters.category);
      if (filters.level) params.append('level', filters.level);

      const response = await api.get(`/achievements?${params.toString()}`);
      const data = response.data.items || response.data;
      setAchievements(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching achievements:', err);
      setAchievements([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch students for autocomplete
  const fetchStudents = async (search) => {
    try {
      const response = await api.get(`/students/search?q=${search}`);
      const data = response.data;
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAchievements();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchStudent = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      searchStudent: value
    }));
    if (value.length > 2) {
      fetchStudents(value);
      setShowStudentDropdown(true);
    } else {
      setShowStudentDropdown(false);
    }
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddStudentSelect = (student) => {
    setFormData(prev => ({
      ...prev,
      studentId: student.id
    }));
    setShowStudentDropdown(false);
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      title: '',
      category: '',
      level: '',
      position: '',
      date: '',
      description: ''
    });
  };

  const handleAddAchievement = async (e) => {
    e.preventDefault();
    try {
      if (selectedAchievement) {
        // Edit mode
        await api.put(`/achievements/${selectedAchievement.id}`, formData);
        setMessage('Achievement updated successfully');
      } else {
        // Add mode
        await api.post('/achievements', formData);
        setMessage('Achievement added successfully');
      }
      setShowAddModal(false);
      resetForm();
      setSelectedAchievement(null);
      fetchAchievements();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error saving achievement:', err);
      setError('Failed to save achievement');
    }
  };

  const handleEditAchievement = (achievement) => {
    setSelectedAchievement(achievement);
    setFormData({
      studentId: achievement.studentId,
      title: achievement.title,
      category: achievement.category,
      level: achievement.level,
      position: achievement.position || '',
      date: achievement.date,
      description: achievement.description || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteAchievement = async () => {
    try {
      await api.delete(`/achievements/${selectedAchievement.id}`);
      setMessage('Achievement deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedAchievement(null);
      fetchAchievements();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting achievement:', err);
      setError('Failed to delete achievement');
    }
  };

  const getCategoryColor = (category) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowDeleteConfirm(false);
    if (!showAddModal) {
      setSelectedAchievement(null);
      resetForm();
    }
  };

  return (
    <div className="achievements-page">
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <Trophy size={24} color="#f59e0b" />
          </div>
          <div className="stat-content">
            <h3>Total Achievements</h3>
            <p className="stat-amount">{stats.totalAchievements || 0}</p>
          </div>
        </div>

        {CATEGORIES.map(category => (
          <div key={category} className="stat-card stat-category" style={{ borderLeftColor: getCategoryColor(category) }}>
            <div className="stat-icon">
              <Award size={20} color={getCategoryColor(category)} />
            </div>
            <div className="stat-content">
              <h3 style={{ textTransform: 'capitalize' }}>{category}</h3>
              <p className="stat-amount">{stats.byCategory?.[category] || 0}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div className="filter-bar">
            <div className="filter-group">
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search student name..."
                  name="searchStudent"
                  value={filters.searchStudent}
                  onChange={handleSearchStudent}
                />
                {showStudentDropdown && students.length > 0 && (
                  <div className="student-dropdown">
                    {students.map(student => (
                      <div
                        key={student.id}
                        className="dropdown-item"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            searchStudent: student.name
                          }));
                          setShowStudentDropdown(false);
                        }}
                      >
                        {student.name} - {student.class}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat} style={{ textTransform: 'capitalize' }}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="level"
                value={filters.level}
                onChange={handleFilterChange}
              >
                <option value="">All Levels</option>
                {LEVELS.map(lv => (
                  <option key={lv} value={lv}>
                    {lv.charAt(0).toUpperCase() + lv.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setSelectedAchievement(null);
                setShowAddModal(true);
              }}
            >
              <Plus size={18} /> Add Achievement
            </button>
          </div>
        </div>
      </div>

      {/* Achievements Cards */}
      <div className="achievements-container">
        {loading ? (
          <div className="empty-state">
            <p>Loading achievements...</p>
          </div>
        ) : achievements.length === 0 ? (
          <div className="empty-state">
            <Trophy size={48} />
            <h3>No achievements found</h3>
            <p>No achievements match your current filters</p>
          </div>
        ) : (
          <div className="cards-grid">
            {achievements.map(achievement => (
              <div
                key={achievement.id}
                className="achievement-card"
                style={{ borderTopColor: getCategoryColor(achievement.category) }}
              >
                <div className="card-header-row">
                  <div className="badges-group">
                    <span
                      className="badge"
                      style={{ backgroundColor: getCategoryColor(achievement.category), color: 'white' }}
                    >
                      {achievement.category.charAt(0).toUpperCase() + achievement.category.slice(1)}
                    </span>
                    <span className="badge badge-info">
                      {achievement.level.charAt(0).toUpperCase() + achievement.level.slice(1)}
                    </span>
                  </div>
                  <div className="card-actions">
                    <button
                      className="btn btn-sm btn-icon"
                      onClick={() => handleEditAchievement(achievement)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn btn-sm btn-icon"
                      onClick={() => {
                        setSelectedAchievement(achievement);
                        setShowDeleteConfirm(true);
                      }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="card-title">{achievement.title}</h3>

                <div className="card-meta">
                  <div className="meta-item">
                    <span className="label">Student:</span>
                    <span className="value">{achievement.studentName}</span>
                  </div>
                  {achievement.position && (
                    <div className="meta-item">
                      <span className="label">Position:</span>
                      <span className="value">{achievement.position}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <span className="label">Date:</span>
                    <span className="value">{new Date(achievement.date).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                {achievement.description && (
                  <p className="card-description">{achievement.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedAchievement ? 'Edit Achievement' : 'Add Achievement'}</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddAchievement}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Student</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search and select student..."
                    value={
                      formData.studentId
                        ? students.find(s => s.id === formData.studentId)?.name || ''
                        : ''
                    }
                    onChange={(e) => {
                      if (e.target.value.length > 2) {
                        fetchStudents(e.target.value);
                        setShowStudentDropdown(true);
                      }
                    }}
                    disabled={!!selectedAchievement}
                    required
                  />
                  {showStudentDropdown && students.length > 0 && (
                    <div className="student-dropdown">
                      {students.map(student => (
                        <div
                          key={student.id}
                          className="dropdown-item"
                          onClick={() => handleAddStudentSelect(student)}
                        >
                          {student.name} - {student.class}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="e.g., First Prize in Science Fair"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      className="form-control"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Level</label>
                    <select
                      className="form-control"
                      name="level"
                      value={formData.level}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Level</option>
                      {LEVELS.map(lv => (
                        <option key={lv} value={lv}>
                          {lv.charAt(0).toUpperCase() + lv.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Position/Rank</label>
                    <input
                      type="text"
                      className="form-control"
                      name="position"
                      value={formData.position}
                      onChange={handleFormChange}
                      placeholder="e.g., 1st, 2nd, 3rd, Gold Medal"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={formData.date}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="4"
                    placeholder="Describe the achievement, competition details, etc..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModals}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedAchievement ? 'Update Achievement' : 'Add Achievement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAchievement && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this achievement?</p>
              <p className="text-muted"><strong>{selectedAchievement.title}</strong></p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteAchievement}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsPage;
